import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ScrollAnimation from './ScrollAnimation';
import ShareButtons from './ShareButtons';
import { getProyecto, proyectos } from '../data/proyectos';
import { useSeo } from '../hooks/useSeo';

const ProyectoDetalle: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const proyecto = getProyecto(slug);
    const [capturaActiva, setCapturaActiva] = useState(0);

    // Al entrar por un link compartido el navegador conserva el scroll de la página
    // anterior: sin esto la ficha abre por la mitad.
    useEffect(() => {
        window.scrollTo(0, 0);
        setCapturaActiva(0);
    }, [slug]);

    useSeo({
        title: proyecto ? `${proyecto.titulo} — Iron Five` : 'Trabajo no encontrado — Iron Five',
        description: proyecto?.subtitulo ?? 'El trabajo que buscás no está en nuestro portafolio.',
        image: proyecto?.imagenSocial ?? proyecto?.imagen,
    });

    if (!proyecto) {
        return (
            <div className="min-h-screen bg-gray-50 pt-32 pb-16">
                <div className="container mx-auto px-6 text-center">
                    <h1 className="text-3xl font-bold font-poppins text-grafito mb-4">
                        No encontramos ese trabajo
                    </h1>
                    <p className="text-gray-600 font-lato mb-8">
                        Puede que el link esté incompleto o que hayamos cambiado la dirección.
                    </p>
                    <Link
                        to="/portafolio"
                        className="inline-flex items-center px-6 py-3 bg-cyan text-white font-montserrat font-semibold rounded-lg hover:bg-opacity-90 transition-colors"
                    >
                        Ver todos los trabajos
                    </Link>
                </div>
            </div>
        );
    }

    const otros = proyectos.filter((p) => p.slug !== proyecto.slug).slice(0, 3);
    const galeria = proyecto.capturas;
    const captura = galeria[capturaActiva];

    return (
        <div className="min-h-screen bg-gray-50 pt-24">
            {/* ── Encabezado ───────────────────────────────────────────────── */}
            <div className="bg-grafito text-white">
                <div className="container mx-auto px-6 py-12">
                    <Link
                        to="/portafolio"
                        className="inline-flex items-center text-sm font-montserrat text-gray-300 hover:text-cyan transition-colors mb-6"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                        Volver al portafolio
                    </Link>

                    <div className="max-w-4xl">
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                            <span className="px-3 py-1 bg-cyan text-white text-xs font-montserrat font-semibold rounded-full">
                                {proyecto.categoria}
                            </span>
                            <span className="text-sm font-lato text-gray-400">{proyecto.anio}</span>
                        </div>

                        <h1 className="text-3xl md:text-4xl font-bold font-poppins mb-4">
                            {proyecto.titulo}
                        </h1>
                        <p className="text-lg md:text-xl font-lato text-gray-300">
                            {proyecto.subtitulo}
                        </p>

                        {proyecto.url && (
                            <a
                                href={proyecto.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center mt-6 px-5 py-2.5 bg-lima text-grafito font-montserrat font-semibold rounded-lg hover:bg-opacity-90 transition-colors"
                            >
                                Ver el sitio en vivo
                                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Métricas ─────────────────────────────────────────────────── */}
            {proyecto.metricas.length > 0 && (
                <div className="bg-white border-b border-gray-200">
                    <div className="container mx-auto px-6 py-8">
                        <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-6">
                            {proyecto.metricas.map((m) => (
                                <div key={m.label} className="text-center">
                                    <p className="text-3xl font-bold font-poppins text-cyan mb-1">{m.valor}</p>
                                    <p className="text-sm font-lato text-gray-600">{m.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="container mx-auto px-6 py-12">
                <div className="max-w-5xl mx-auto">
                    {/* ── Galería ──────────────────────────────────────────── */}
                    {galeria.length > 0 && (
                        <ScrollAnimation>
                            <div className="mb-12">
                                <div className="rounded-xl overflow-hidden shadow-lg bg-white">
                                    <img
                                        src={captura.src}
                                        alt={captura.alt}
                                        className="w-full object-cover"
                                    />
                                </div>

                                {captura.caption && (
                                    <p className="text-sm font-lato text-gray-600 italic mt-3 px-1">
                                        {captura.caption}
                                    </p>
                                )}

                                {galeria.length > 1 && (
                                    <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                                        {galeria.map((c, i) => (
                                            <button
                                                key={c.src}
                                                type="button"
                                                onClick={() => setCapturaActiva(i)}
                                                aria-label={`Ver captura ${i + 1}: ${c.alt}`}
                                                aria-current={i === capturaActiva}
                                                className={`flex-shrink-0 w-28 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                                                    i === capturaActiva
                                                        ? 'border-cyan opacity-100'
                                                        : 'border-transparent opacity-60 hover:opacity-100'
                                                }`}
                                            >
                                                <img src={c.src} alt="" className="w-full h-full object-cover" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </ScrollAnimation>
                    )}

                    {/* Sistemas internos: se dice por qué no hay capturas, en vez de
                        dejar la ficha con un hueco que parece un error de carga. */}
                    {proyecto.confidencial && galeria.length === 0 && (
                        <ScrollAnimation>
                            <div className="mb-12 rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center">
                                <svg className="w-10 h-10 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                <p className="font-montserrat font-semibold text-grafito mb-1">
                                    Sin capturas públicas
                                </p>
                                <p className="text-sm font-lato text-gray-600 max-w-lg mx-auto">
                                    Es un sistema en producción y sus pantallas muestran datos reales de
                                    clientes. Podemos mostrarlo en vivo en una demo, con datos de prueba.
                                </p>
                            </div>
                        </ScrollAnimation>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* ── Relato ───────────────────────────────────────── */}
                        <div className="lg:col-span-2">
                            <ScrollAnimation>
                                <h2 className="text-2xl font-bold font-poppins text-grafito mb-6">
                                    Cómo fue
                                </h2>
                                <div className="space-y-4">
                                    {proyecto.historia.map((parrafo, i) => (
                                        <p key={i} className="text-gray-700 font-lato leading-relaxed">
                                            {parrafo}
                                        </p>
                                    ))}
                                </div>
                            </ScrollAnimation>

                            {(proyecto.desafio || proyecto.solucion) && (
                                <ScrollAnimation delay={0.2}>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
                                        {proyecto.desafio && (
                                            <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-gray-300">
                                                <h3 className="font-montserrat font-semibold text-grafito mb-2">
                                                    El desafío
                                                </h3>
                                                <p className="text-sm text-gray-600 font-lato leading-relaxed">
                                                    {proyecto.desafio}
                                                </p>
                                            </div>
                                        )}
                                        {proyecto.solucion && (
                                            <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-cyan">
                                                <h3 className="font-montserrat font-semibold text-grafito mb-2">
                                                    Lo que hicimos
                                                </h3>
                                                <p className="text-sm text-gray-600 font-lato leading-relaxed">
                                                    {proyecto.solucion}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </ScrollAnimation>
                            )}

                            <ScrollAnimation delay={0.3}>
                                <ShareButtons titulo={`${proyecto.titulo} — Iron Five`} variante="panel" />
                            </ScrollAnimation>
                        </div>

                        {/* ── Ficha técnica ────────────────────────────────── */}
                        <aside>
                            <ScrollAnimation delay={0.2}>
                                <div className="bg-white rounded-xl p-6 shadow-sm lg:sticky lg:top-28">
                                    <h3 className="font-montserrat font-semibold text-grafito mb-4">
                                        Ficha del proyecto
                                    </h3>

                                    <dl className="space-y-4 text-sm font-lato">
                                        {proyecto.cliente && (
                                            <div>
                                                <dt className="text-gray-500 mb-0.5">Cliente</dt>
                                                <dd className="text-grafito">{proyecto.cliente}</dd>
                                            </div>
                                        )}
                                        <div>
                                            <dt className="text-gray-500 mb-0.5">Año</dt>
                                            <dd className="text-grafito">{proyecto.anio}</dd>
                                        </div>
                                        {proyecto.rol && (
                                            <div>
                                                <dt className="text-gray-500 mb-0.5">Nuestro rol</dt>
                                                <dd className="text-grafito">{proyecto.rol}</dd>
                                            </div>
                                        )}
                                        {proyecto.duracion && (
                                            <div>
                                                <dt className="text-gray-500 mb-0.5">Duración</dt>
                                                <dd className="text-grafito">{proyecto.duracion}</dd>
                                            </div>
                                        )}
                                    </dl>

                                    <div className="mt-6 pt-6 border-t border-gray-200">
                                        <p className="text-sm text-gray-500 font-lato mb-3">Tecnologías</p>
                                        <div className="flex flex-wrap gap-2">
                                            {proyecto.tecnologias.map((t) => (
                                                <span
                                                    key={t}
                                                    className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-montserrat rounded"
                                                >
                                                    {t}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mt-6 pt-6 border-t border-gray-200">
                                        <p className="text-sm text-gray-600 font-lato mb-3">
                                            ¿Tenés algo parecido en mente?
                                        </p>
                                        <Link
                                            to="/#contacto"
                                            className="block text-center px-4 py-2.5 bg-cyan text-white font-montserrat font-semibold text-sm rounded-lg hover:bg-opacity-90 transition-colors"
                                        >
                                            Charlemos
                                        </Link>
                                    </div>
                                </div>
                            </ScrollAnimation>
                        </aside>
                    </div>

                    {/* ── Otros trabajos ───────────────────────────────────── */}
                    <ScrollAnimation>
                        <div className="mt-16 pt-12 border-t border-gray-200">
                            <h2 className="text-2xl font-bold font-poppins text-grafito mb-8">
                                Otros trabajos
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {otros.map((p) => (
                                    <Link
                                        key={p.slug}
                                        to={`/portafolio/${p.slug}`}
                                        className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                                    >
                                        <div className="h-36 bg-gray-200 overflow-hidden">
                                            <img
                                                src={p.imagen}
                                                alt={p.titulo}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            />
                                        </div>
                                        <div className="p-4">
                                            <h3 className="font-montserrat font-semibold text-grafito text-sm mb-1">
                                                {p.titulo}
                                            </h3>
                                            <p className="text-xs text-gray-500 font-lato">{p.anio}</p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </ScrollAnimation>
                </div>
            </div>
        </div>
    );
};

export default ProyectoDetalle;
